import {
  BoltIcon,
  BoltSlashIcon,
  EyeIcon,
  EyeSlashIcon,
  PencilSquareIcon,
  PlusIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { FieldOrigin, LoanFieldProps } from 'config'
import { InputTypeOfLoans } from 'config/loan.input.fields.constants'
import React, { useMemo, useState } from 'react'
import { Section, Tooltip } from 'stories/components'
import { RenderInput } from 'utils/RenderInput'

import { VisiblePropertyModal } from '../VisiblePropertyModal'

export const BaseAdditionalInputs = ({
  data,
  fieldTitles,
  fieldInputs,
  onUpdate,

  title,
  fieldOrigin,
  propertyKey,
  loanFieldProps,
}: {
  data: any
  fieldTitles: Record<string, { title: string; fields: Record<string, string> }>
  fieldInputs: InputTypeOfLoans
  onUpdate: Function

  title: string
  fieldOrigin: FieldOrigin
  propertyKey: string
  loanFieldProps: LoanFieldProps
}) => {
  const properties = data[propertyKey] || {}
  const [selectedFieldKey, setSelectedFieldKey] = useState<string | null>()

  const getInput = (key: string | null) => {
    if (!key) return {}
    const { fieldKey } = properties[key].linkedField
    return fieldInputs[fieldOrigin][fieldKey]
  }

  const selectedFieldData = useMemo(() => {
    if (!selectedFieldKey || !properties[selectedFieldKey])
      return {
        linkedField: {
          fieldOrigin,
          fieldKey: '',
        },
      }
    return {
      ...getInput(selectedFieldKey),
      ...properties[selectedFieldKey],
    }
  }, [selectedFieldKey])

  const onAddNewField = () => {
    setSelectedFieldKey(null)
  }

  const onCloseVisiblePropertyModal = (newItem: any) => {
    setSelectedFieldKey(undefined)

    if (!newItem) return
    const newId = selectedFieldKey || newItem?.linkedField?.fieldKey || Date.now()

    const newData = cloneDeep(data)
    if (!newData[propertyKey]) newData[propertyKey] = {}
    newData[propertyKey][newId] = {
      ...(newData[propertyKey][newId] || {}),
      ...newItem,
    }
    onUpdate(newData)
  }

  const onToggleProperty = (key: string, property: 'visible' | 'required', currentValue: boolean) => {
    const newData = cloneDeep(data)
    newData[propertyKey][key] = {
      ...(newData[propertyKey][key] || {}),
      [property]: !currentValue,
    }
    onUpdate(newData)
  }

  return (
    <div>
      <Section
        title={title}
        additionalElements={
          <div className="flex gap-2">
            <Tooltip message="Add a new field">
              <span className="btn-icon" onClick={() => onAddNewField()}>
                <PlusIcon className="w-4 h-4" />
              </span>
            </Tooltip>
          </div>
        }
      />
      <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mt-2`}>
        {Object.keys(properties).map((key) => {
          const { fieldKey } = properties[key].linkedField
          const input: any = {
            ...fieldInputs[fieldOrigin][fieldKey],
            ...properties[key],
          }
          const { visible = true, defaultValue } = input
          return (
            <React.Fragment key={`loan-structure-additional-property-${key}`}>
              <div
                className={`grid grid-cols-1 items-center ${visible ? 'opacity-100' : 'opacity-30 z-10'} ${
                  input.span ? `col-span-${input.span}` : ''
                } mb-2`}
              >
                <RenderInput
                  key={`loan-structure-additional-property-${key}`}
                  input={{
                    ...input,
                    value: defaultValue,
                    additionalElements: (
                      <div className="flex">
                        {input.tooltip && (
                          <Tooltip message={input.tooltip}>
                            <span className="btn-icon">
                              <QuestionMarkCircleIcon className="w-4 h-4 text-gray-500" />
                            </span>
                          </Tooltip>
                        )}
                        <Tooltip message={visible ? 'Visible' : 'Hidden'}>
                          <span className="btn-icon" onClick={() => onToggleProperty(key, 'visible', visible)}>
                            {visible ? (
                              <EyeIcon className="w-4 h-4 text-gray-500" />
                            ) : (
                              <EyeSlashIcon className="w-4 h-4 text-gray-500" />
                            )}
                          </span>
                        </Tooltip>
                        <span className="btn-icon" onClick={() => setSelectedFieldKey(key)}>
                          <PencilSquareIcon className="w-4 h-4 text-gray-500" />
                        </span>
                        <Tooltip message={input.required ? 'Required' : 'Not Required'}>
                          <span className="btn-icon" onClick={() => onToggleProperty(key, 'required', input.required)}>
                            {input.required ? (
                              <BoltIcon className="w-4 h-4 text-gray-500" />
                            ) : (
                              <BoltSlashIcon className="w-4 h-4 text-gray-500" />
                            )}
                          </span>
                        </Tooltip>
                      </div>
                    ),
                  }}
                  Key={`${key}`}
                  onChange={(_: any, newValue: any) => {
                    const newData = cloneDeep(data)
                    newData[propertyKey][key] = {
                      ...(newData[propertyKey][key] || {}),
                      defaultValue: newValue,
                    }
                    onUpdate(newData)
                  }}
                />
              </div>
            </React.Fragment>
          )
        })}
      </div>

      {selectedFieldKey !== undefined && (
        <VisiblePropertyModal
          field={{
            fieldOrigin,
            fieldKey: selectedFieldKey || '',
            title:
              selectedFieldData.title || fieldTitles[FieldOrigin.LoanStructure]?.fields[selectedFieldKey || ''] || '',
          }}
          data={selectedFieldData}
          inputProps={getInput(selectedFieldKey) as any}
          onClose={onCloseVisiblePropertyModal}
          fieldTitles={fieldTitles}
          fieldInputs={fieldInputs}
          properties={{ linkedField: { showLinkedField: true, fieldOrigin: [fieldOrigin], title: 'Match Field to' } }}
          loanFieldProps={loanFieldProps}
          showAdjustments={false}
        />
      )}
    </div>
  )
}
